import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './SessionDisplay.css';
import './SessionInvestigationDisplay.css';
import '../../ui/activityViewer.css';
import MediaControls from './MediaControls.jsx';
import AddressDisplay from './AddressDisplay.jsx';
import RendezVousParticipantPlayer from './RendezVousParticipantPlayer.jsx';
import RendezVousMediaLanes from './RendezVousMediaLanes.jsx';
import TrackMap from '../../ui/TrackMap.jsx';
import { useActivityClock } from '../../ui/useActivityClock.js';
import { buildRendezVousJournal, rendezVousPhases, phasesGradient } from '../../adapters/rendezVousJournal.js';

const clockText = ms => new Date(ms).toLocaleTimeString('fr-FR');
const elapsedText = seconds => {
    const s = Math.max(0, Math.floor(seconds || 0));
    const h = Math.floor(s / 3600);
    const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
    const ss = String(s % 60).padStart(2, '0');
    return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};
const ALL = 'all';

function toggleFullscreen(element) {
    if (!element) return;
    if (document.fullscreenElement === element) document.exitFullscreen?.().catch(() => {});
    else element.requestFullscreen?.().catch(() => {});
}

const FullscreenButton = ({ target }) => (
    <button type="button" className="sid-fullscreen" title="Plein écran" aria-label="Plein écran"
        onClick={() => toggleFullscreen(target.current)}>⛶</button>
);

/** Photos en vignettes, chargées (et déchiffrées) à la demande par l'application. */
function PhotoGallery({ photos, t, fetchBlob, onSeek }) {
    const [urls, setUrls] = useState({});
    const [preview, setPreview] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const created = [];
        photos.forEach(async photo => {
            try {
                const url = URL.createObjectURL(await fetchBlob(photo.url));
                created.push(url);
                if (!cancelled) setUrls(previous => ({ ...previous, [photo.id]: url }));
            } catch (_) {
                if (!cancelled) setUrls(previous => ({ ...previous, [photo.id]: null }));
            }
        });
        return () => { cancelled = true; created.forEach(url => URL.revokeObjectURL(url)); };
    }, [photos, fetchBlob]);
    if (photos.length === 0) return <p className="sid-empty">Aucune photo.</p>;
    return (
        <>
            <div className="rid-photos">
                {photos.map(photo => (
                    <button type="button" key={photo.id} className={`rid-photo ${photo.t <= t ? '' : 'rid-future'}`}
                        style={{ borderColor: photo.color }}
                        onClick={() => { onSeek(photo.t); if (urls[photo.id]) setPreview(photo); }}>
                        {urls[photo.id] ? <img src={urls[photo.id]} alt={`Prise par ${photo.owner}`} />
                            : <span>{urls[photo.id] === null ? 'Indisponible' : 'Chargement…'}</span>}
                        <small>{photo.owner} · {clockText(photo.t)}</small>
                    </button>
                ))}
            </div>
            {preview && (
                <div className="av-lightbox" role="dialog" onClick={() => setPreview(null)}>
                    <img src={urls[preview.id]} alt={`Prise par ${preview.owner}`} />
                    <span>{preview.owner} — {clockText(preview.t)}</span>
                </div>
            )}
        </>
    );
}

/**
 * Vue enquête d'un rendez-vous : en haut un lecteur par personne (ses vidéos,
 * ses sons, ses photos) et la carte au milieu ; en bas la timeline commune, qui
 * les pilote, avec une ligne par personne qui situe chaque capture, et les
 * messages, qui suivent le curseur ; en faisant défiler, les photos et le
 * journal. Le sélecteur de participant filtre la carte, les phases et le journal.
 */
export default function RendezVousInvestigationDisplay({ bundle, hlsConfig, fetchBlob, header = null, toolbar = null, alerts = null }) {
    const { t, clock } = useActivityClock(bundle?.startMs ?? null, bundle?.endMs ?? null);
    const tracks = useMemo(() => bundle?.tracks || [], [bundle]);
    const participants = useMemo(() => tracks.filter(track => track.kind !== 'responder'), [tracks]);
    const [focus, setFocus] = useState(ALL);
    const shown = useMemo(() => (focus === ALL ? tracks : tracks.filter(track => track.id === focus)), [tracks, focus]);
    // Le participant choisi voit ses données ; les messages restent la conversation entière.
    const view = useMemo(() => (bundle ? { ...bundle, tracks: shown } : null), [bundle, shown]);

    const mapSectionRef = useRef(null);
    const messagesRef = useRef(null);
    const messagesListRef = useRef(null);

    const start = bundle?.startMs ?? 0;
    const end = bundle?.endMs ?? start;
    // Faits communs (rencontre, fin) et ceux du participant choisi.
    const journal = useMemo(() => {
        const ids = new Set(shown.map(track => track.id));
        return buildRendezVousJournal(view).filter(entry => !entry.trackId || ids.has(entry.trackId));
    }, [view, shown]);
    const gradient = useMemo(() => phasesGradient(rendezVousPhases(view), start, end), [view, start, end]);
    const seek = useCallback(epochMs => clock.seek(epochMs), [clock]);

    const total = Math.max(0, (end - start) / 1000);
    const elapsed = Math.max(0, (t - start) / 1000);
    const timelineState = {
        value: elapsed, max: total, minTs: start, maxTs: end, gradient,
        display: `⏱ ${elapsedText(elapsed)} / ${elapsedText(total)}`,
    };
    const messages = useMemo(() => bundle?.messages || [], [bundle]);
    const colorOf = useMemo(() => new Map(tracks.map(track => [track.id, track.color])), [tracks]);
    const visibleMessages = useMemo(() => messages.filter(message => message.t <= t), [messages, t]);
    useLayoutEffect(() => {
        const list = messagesListRef.current;
        if (list) list.scrollTop = list.scrollHeight;
    }, [visibleMessages.length]);
    // Enregistrements (vidéo, son) des personnes affichées, dans l'ordre chronologique.
    const recordings = useMemo(() => shown
        .flatMap(track => (track.media || []).map(media => ({ ...media, color: track.color, owner: track.label })))
        .sort((a, b) => a.startMs - b.startMs), [shown]);
    const photos = useMemo(() => shown
        .flatMap(track => (track.photos || []).map(photo => ({ ...photo, color: track.color, owner: track.label })))
        .sort((a, b) => a.t - b.t), [shown]);

    if (!bundle) return <div className="sid-root sid-message"><p>Aucune donnée.</p></div>;

    return (
        <div className="rid-page">
            <div className="sid-root rid-screen">
                <header className="sid-header">
                    <div className="sid-header-left">
                        {header}
                        <div className="rid-focus" role="group" aria-label="Participant">
                            {[{ id: ALL, label: 'Les deux', color: null }, ...participants].map(track => (
                                <button type="button" key={track.id} className={focus === track.id ? 'rid-active' : ''}
                                    style={track.color ? { borderColor: track.color } : undefined} onClick={() => setFocus(track.id)}
                                    title={track.email || undefined}>
                                    {track.color && <i style={{ background: track.color }} />}{track.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="sid-toolbar">{toolbar}</div>
                </header>

                {/* La carte en haut, et les deux lecteurs de personnes côte à côte en dessous sur la même ligne. */}
                <main className="sid-top rid-top">
                    <section ref={mapSectionRef} className="sid-map rid-map">
                        <div className="map-container" style={{ position: 'relative', width: '100%' }}>
                            {bundle.address && <AddressDisplay text={bundle.address} />}
                            <TrackMap bundle={view} t={t} onSelectTrack={setFocus} />
                        </div>
                        <FullscreenButton target={mapSectionRef} />
                    </section>
                    <div className="rid-players-row">
                        {participants[0] && <RendezVousParticipantPlayer track={participants[0]} bundle={bundle} clock={clock} t={t} hlsConfig={hlsConfig} fetchBlob={fetchBlob} />}
                        {participants[1] && <RendezVousParticipantPlayer track={participants[1]} bundle={bundle} clock={clock} t={t} hlsConfig={hlsConfig} fetchBlob={fetchBlob} />}
                    </div>
                </main>

                <div className="sid-bottom">
                    <section className="sid-timeline">
                        <MediaControls
                            timelineState={timelineState}
                            onPlay={clock.play}
                            onPause={clock.pause}
                            onStart={() => seek(start)}
                            onTimelineInput={value => seek(start + value * 1000)}
                            onTimelineChange={value => seek(start + value * 1000)}
                            idPrefix="rdv-"
                        />
                        <RendezVousMediaLanes tracks={tracks} start={start} end={end} t={t} onSeek={seek} alerts={alerts} />
                    </section>

                    <section ref={messagesRef} className="sid-journal" aria-label="Messages du rendez-vous">
                        <h2>Messages <span>{visibleMessages.length}/{messages.length}</span></h2>
                        <FullscreenButton target={messagesRef} />
                        {visibleMessages.length === 0 ? <p className="sid-empty">Aucun message à cette heure.</p> : (
                            <ol ref={messagesListRef} className="rid-messages">
                                {visibleMessages.map(message => (
                                    <li key={message.id}>
                                        <button type="button" onClick={() => seek(message.t)} style={{ borderLeftColor: colorOf.get(message.trackId) || '#64748b' }}>
                                            <span className="rid-message-meta">
                                                <strong style={{ color: colorOf.get(message.trackId) || undefined }}>{message.senderName}</strong>
                                                <time>{clockText(message.t)}</time>
                                            </span>
                                            <span className="rid-message-text">{message.text}</span>
                                        </button>
                                    </li>
                                ))}
                            </ol>
                        )}
                    </section>
                </div>
                <div className="rid-scroll-hint" aria-hidden="true">Photos et journal ↓</div>
            </div>

            <section className="rid-below">
                <h2>Photos et vidéos <span>{photos.length} photo(s) · {recordings.length} enregistrement(s)</span></h2>
                {recordings.length > 0 && (
                    <div className="rid-recordings">
                        {recordings.map(media => (
                            <button type="button" key={media.id} className={`rid-recording ${media.startMs <= t ? '' : 'rid-future'}`}
                                style={{ borderColor: media.color }} onClick={() => { seek(media.startMs); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                                <strong>{media.type === 'video' ? '🎥' : '🎙️'} {media.label}</strong>
                                <small>{clockText(media.startMs)} → {clockText(media.endMs || media.startMs)}{media.endMs > media.startMs ? ` · ${elapsedText((media.endMs - media.startMs) / 1000)}` : ''}</small>
                                <small>Cliquer pour la lire dans le lecteur de {media.owner}</small>
                            </button>
                        ))}
                    </div>
                )}
                <PhotoGallery photos={photos} t={t} fetchBlob={fetchBlob} onSeek={seek} />
                <h2>Journal <span>{journal.length} ligne(s){focus === ALL ? '' : ` · ${shown[0]?.label || ''}`}</span></h2>
                <ol className="rid-full-journal">
                    {journal.map((entry, index) => (
                        <li key={`${entry.t}-${index}`} className={entry.t <= t ? '' : 'rid-future'}>
                            <button type="button" onClick={() => { seek(entry.t); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                                <span className="sid-dot" style={{ background: entry.color }} />
                                <time>{new Date(entry.t).toLocaleString('fr-FR')}</time>
                                <span>{entry.text}</span>
                            </button>
                        </li>
                    ))}
                </ol>
            </section>
        </div>
    );
}
